import type { AlertSeverity, OperationalAlertDto } from '@genoma/shared';

import { Link } from 'react-router-dom';

import { useI18n } from '../../i18n/index.tsx';

/**
 * Operational alerts.
 *
 * A status colour never carries the meaning on its own — on this warm surface
 * the amber sits below 3:1 anyway. Every row pairs the colour with a glyph and
 * a written severity, and the text itself stays in the ink tokens.
 */
const SEVERITY_MARK: Record<AlertSeverity, { glyph: string; dot: string }> = {
  CRITICAL: { glyph: '●', dot: 'text-[var(--color-critical)]' },
  WARNING: { glyph: '▲', dot: 'text-[var(--color-warning)]' },
  INFO: { glyph: '■', dot: 'text-teal' },
};

type Props = {
  alerts: OperationalAlertDto[];
  /**
   * How many to show before offering the rest on their own page.
   *
   * Left unbounded, a mid-cycle dashboard grows a column of near-identical
   * warnings taller than everything beside it — and a list nobody can finish
   * reading is one nobody reads. Three is what fits next to the panel it sits
   * beside; the page has them all.
   */
  limit?: number;
};

export const AlertList = ({ alerts, limit }: Props) => {
  const { t } = useI18n();
  const shown = limit ? alerts.slice(0, limit) : alerts;
  const hidden = alerts.length - shown.length;

  if (alerts.length === 0) {
    return (
      <p className="rounded-2xl border border-line bg-paper px-4 py-3.5 text-sm text-soft shadow-card">
        {t('dashboard.noAlerts')}
      </p>
    );
  }

  const describe = (alert: OperationalAlertDto): string => {
    const segments = (alert.segments ?? [])
      .map((segment) => t(`relationships.${segment}`))
      .join(', ');

    switch (alert.kind) {
      case 'REPORT_BLOCKED':
        return t('alerts.reportBlocked', {
          name: alert.participantName ?? '',
          count: alert.count ?? 0,
        });
      case 'SEGMENT_AT_RISK':
        return t('alerts.segmentAtRisk', {
          name: alert.participantName ?? '',
          segments,
        });
      case 'KEY_LEADER_DELAYED':
        return t('alerts.keyLeaderDelayed', {
          name: alert.participantName ?? '',
          rate: alert.count ?? 0,
        });
      case 'SELF_ASSESSMENT_MISSING':
        return t('alerts.selfAssessmentMissing', { name: alert.participantName ?? '' });
      case 'LOW_PARTICIPATION':
        return t('alerts.lowParticipation', { count: alert.count ?? 0 });
      case 'RATER_NOT_STARTED':
        return t('alerts.raterNotStarted', { count: alert.count ?? 0 });
      case 'REPORT_AWAITING_APPROVAL':
        return t('alerts.reportAwaitingApproval', { count: alert.count ?? 0 });
      case 'SURVEY_READY_TO_CLOSE':
        return t('alerts.surveyReadyToClose');
      case 'CLOSING_SOON':
        return t('alerts.closingSoon', { count: alert.count ?? 0 });
      case 'CLOSE_OVERDUE':
        // Zero days over is the deadline day itself, which reads as "today"
        // rather than as "0 days ago".
        return (alert.count ?? 0) === 0
          ? t('alerts.closeDueToday')
          : t('alerts.closeOverdue', { count: alert.count ?? 0 });
      default:
        return alert.kind;
    }
  };

  return (
    <ul className="list-none overflow-hidden rounded-2xl border border-line bg-paper shadow-card">
      {shown.map((alert) => {
        const mark = SEVERITY_MARK[alert.severity];
        return (
          <li
            key={alert.id}
            className="flex items-start gap-3 border-t border-line-soft px-4 py-3 first:border-t-0"
          >
            <span aria-hidden className={`mt-px text-[11px] leading-5 ${mark.dot}`}>
              {mark.glyph}
            </span>
            <span className="min-w-0">
              <span className="font-mono text-[9.5px] tracking-[0.12em] text-faint uppercase">
                {t(`alertSeverity.${alert.severity}`)}
              </span>
              <span className="block text-[13.5px] text-ink">{describe(alert)}</span>
            </span>
          </li>
        );
      })}

      {hidden > 0 && (
        <li className="border-t border-line-soft">
          <Link
            to="/alertas"
            className="flex items-center justify-between px-4 py-3 text-[13px] font-semibold text-teal transition hover:bg-tint-soft"
          >
            {t('dashboard.seeAllAlerts', { count: hidden })}
            <span aria-hidden>→</span>
          </Link>
        </li>
      )}
    </ul>
  );
};
